import { useState } from "react";
import { SectionHead } from "./Shared";

type Status = "idle" | "success" | "error";

export default function NewsletterSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐字段：命中则不提交，仅给出与正常一致的反馈
    const honey = String(formData.get("website_alt") || "").trim();
    if (honey) {
      setStatus("success");
      setMessage("订阅成功，已把今天的第一波新瓜送到你的邮箱。");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    try {
      const res = await fetch("https://readdy.ai/api/form/dagna5htto0ceqqni0rg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta || {}) as Record<string, unknown>;
      const serverMsg =
        String(meta?.message || "") ||
        String(parsed?.message || "") ||
        String(meta?.detail || "");
      const code = String(parsed?.code || "");

      const isSpam =
        res.status === 200 &&
        code === "InvalidParameter" &&
        serverMsg.toLowerCase().includes("spam");

      if (res.ok && (code === "OK" || code === "") && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，今天的第一波新瓜马上送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "订阅失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，订阅失败，请稍后再试。");
    }
  };

  return (
    <section id="join" className="w-full py-10 md:py-14" aria-label="订阅每日瓜料">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-2xl bg-primary-600 px-6 py-10 md:px-12 md:py-14">
          <i className="ri-bubble-chart-fill absolute -right-6 -top-6 text-[120px] text-primary-500/40" />
          <i className="ri-bubble-chart-fill absolute -bottom-10 left-10 text-[90px] text-primary-400/30" />
          <div className="relative grid grid-cols-1 items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <div>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold tracking-wide">
                  <span className="inline-block h-5 w-1 rounded-full bg-accent-400" />
                  <span className="text-accent-300">NEWSLETTER</span>
                </div>
                <h2 className="font-heading text-2xl font-black leading-tight text-background-50 md:text-[32px]">
                  订阅每日瓜料 · 第一手新瓜不错过
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-7 text-background-100/90 md:text-base">
                  每天早上 8 点，把前一夜到今晨的重点瓜与热榜整理成一份清爽邮件送到你手上。零打扰，想看才点开。
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <form
                id="newsletterForm"
                data-readdy-form="每日瓜料订阅"
                onSubmit={handleSubmit}
                className="subscribe-form relative rounded-2xl bg-background-50/95 p-5 md:p-6"
              >
                <p className="text-sm font-semibold text-foreground-900">免费订阅 · 随时可退订</p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <label htmlFor="nl-email" className="flex-1">
                    <span className="sr-only">邮箱地址</span>
                    <input
                      id="nl-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="请输入你的邮箱地址"
                      className="w-full rounded-full border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                  <button
                    type="submit"
                    className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    立即订阅
                  </button>
                </div>
                {/* 反垃圾蜜罐字段 */}
                <label htmlFor="website_alt" className="website_alt" aria-hidden="true">
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>

                {status === "success" ? (
                  <p className="mt-3 flex items-center gap-2 rounded-lg bg-accent-100 px-3 py-2 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line" /> {message}
                  </p>
                ) : null}
                {status === "error" ? (
                  <p className="mt-3 flex items-center gap-2 rounded-lg bg-primary-100 px-3 py-2 text-sm text-primary-800">
                    <i className="ri-error-warning-line" /> {message}
                  </p>
                ) : null}
                <p className="mt-3 text-xs leading-5 text-foreground-500">
                  订阅即表示同意我们仅为推送每日瓜料摘要而使用你的邮箱，绝不会向第三方共享。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}